import React, { useEffect, useRef, useState } from 'react';
import { content, colors } from '../data/content';
import { Store, ShoppingBag, Fish, MapPin, CheckCircle } from 'lucide-react';

const WhereToBuy = ({ language }) => {
  const currentContent = content[language].whereToBuy;
  const isRTL = language === 'ar';
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            currentContent.locations.forEach((_, index) => {
              setTimeout(() => {
                setVisibleItems((prev) => [...prev, index]);
              }, index * 100);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, [currentContent.locations]);

  const iconMap = {
    store: Store,
    shoppingBag: ShoppingBag,
    cart: ShoppingBag,
    fish: Fish
  };

  return (
    <section 
      id="whereToBuy" 
      ref={sectionRef}
      style={{
        background: `linear-gradient(180deg, #ffffff 0%, #f0f9ff 50%, #ffffff 100%)`,
        padding: 'clamp(60px, 10vw, 120px) clamp(20px, 4vw, 40px)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Decoration */}
      <div style={{
        position: 'absolute',
        top: 0,
        [isRTL ? 'right' : 'left']: 0,
        width: '300px',
        height: '300px',
        background: `radial-gradient(circle, ${colors.secondary}08 0%, transparent 70%)`,
        borderRadius: '50%',
        filter: 'blur(60px)',
        transform: 'translate(-50%, -50%)'
      }}></div>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 clamp(20px, 4vw, 40px)',
        position: 'relative',
        zIndex: 10
      }}>
        {/* Section Header */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: 'clamp(48px, 8vw, 80px)',
          animation: 'fadeInUp 0.8s ease-out'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            background: `linear-gradient(135deg, ${colors.secondary}10 0%, ${colors.primary}10 100%)`,
            borderRadius: '50px',
            marginBottom: '20px',
            border: `1px solid ${colors.secondary}20`
          }}>
            <MapPin size={18} style={{ color: colors.secondary }} />
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: colors.secondary,
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
            }}>
              {isRTL ? 'مواقع البيع' : 'Where to Find'}
            </span>
          </div>

          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 800,
            color: colors.secondary,
            marginBottom: 'clamp(16px, 3vw, 24px)',
            fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
            background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: isRTL ? 1.65 : 1.2
          }}>
            {currentContent.title}
          </h2>
          <p style={{
            fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
            color: colors.text,
            fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: isRTL ? 1.8 : 1.7
          }}>
            {currentContent.subtitle}
          </p>
        </div>

        {/* Location Types Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 'clamp(24px, 5vw, 32px)',
          marginBottom: 'clamp(48px, 8vw, 80px)'
        }}>
          {currentContent.locations.map((location, index) => {
            const IconComponent = iconMap[location.icon] || Store;
            const isVisible = visibleItems.includes(index);
            
            return (
              <div
                key={index}
                style={{
                  background: 'white',
                  borderRadius: '24px',
                  padding: 'clamp(32px, 5vw, 40px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
                  animation: isVisible ? 'fadeInUp 0.6s ease-out' : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-12px) scale(1.03)';
                  e.currentTarget.style.boxShadow = `0 20px 40px ${location.color}30`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
                }}
              >
                {/* Top Border Gradient */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${location.color} 0%, ${location.color}80 100%)`,
                  borderRadius: '24px 24px 0 0'
                }}></div>

                {/* Icon Container */}
                <div style={{
                  width: 'clamp(80px, 12vw, 100px)',
                  height: 'clamp(80px, 12vw, 100px)',
                  borderRadius: '20px',
                  background: `linear-gradient(135deg, ${location.color}15 0%, ${location.color}05 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto clamp(24px, 4vw, 32px)',
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
                  e.currentTarget.style.background = `linear-gradient(135deg, ${location.color}25 0%, ${location.color}15 100%)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                  e.currentTarget.style.background = `linear-gradient(135deg, ${location.color}15 0%, ${location.color}05 100%)`;
                }}
                >
                  <IconComponent 
                    size={typeof window !== 'undefined' && window.innerWidth < 768 ? 40 : 48} 
                    style={{ 
                      color: location.color,
                      transition: 'all 0.3s ease'
                    }} 
                  />
                </div>

                <h3 style={{
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontWeight: 700,
                  color: colors.secondary,
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  marginBottom: '12px',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = location.color;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.secondary;
                }}
                >
                  {location.title}
                </h3>
              </div>
            );
          })}
        </div>

        {/* Cities Section */}
        <div style={{
          background: 'white',
          borderRadius: '32px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
          padding: 'clamp(40px, 6vw, 60px) clamp(32px, 5vw, 48px)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '200px',
            height: '200px',
            background: `radial-gradient(circle, ${colors.primary}05 0%, transparent 70%)`,
            borderRadius: '50%',
            filter: 'blur(40px)',
            transform: 'translate(30%, -30%)'
          }}></div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'clamp(12px, 2vw, 16px)',
            marginBottom: 'clamp(32px, 5vw, 48px)',
            position: 'relative',
            zIndex: 10
          }}>
            <MapPin size={typeof window !== 'undefined' && window.innerWidth < 768 ? 28 : 36} style={{ color: colors.primary }} />
            <h3 style={{
              fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
              fontWeight: 700,
              color: colors.secondary,
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
              margin: 0
            }}>
              {currentContent.locationsTitle || (isRTL ? 'مواقع الإمارات' : 'UAE Locations')}
            </h3>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 'clamp(20px, 4vw, 28px)',
            position: 'relative',
            zIndex: 10
          }}>
            {currentContent.cities.map((city, index) => (
              <div
                key={index}
                style={{
                  borderRadius: '20px',
                  padding: 'clamp(24px, 4vw, 32px)',
                  textAlign: 'center',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: '2px solid transparent',
                  position: 'relative',
                  overflow: 'hidden',
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px) scale(1.05)';
                  e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.secondary}10 100%)`;
                  e.currentTarget.style.borderColor = colors.primary;
                  e.currentTarget.style.boxShadow = `0 12px 32px ${colors.primary}20`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)';
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ 
                  fontSize: 'clamp(2.5rem, 6vw, 3.5rem)', 
                  marginBottom: 'clamp(8px, 2vw, 12px)',
                  lineHeight: 1
                }}>
                  {city.icon}
                </div>
                <p style={{
                  fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                  fontWeight: 700,
                  color: colors.secondary,
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  margin: 0
                }}>
                  {city.name}
                </p>
                
                {/* Check Badge */}
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  [isRTL ? 'left' : 'right']: '12px',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: colors.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transform: 'scale(0)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0';
                  e.currentTarget.style.transform = 'scale(0)';
                }}
                >
                  <CheckCircle size={16} style={{ color: 'white' }} />
                </div>
              </div>
            ))}
          </div>

          {currentContent.mapImage && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: 'clamp(32px, 5vw, 48px)',
              position: 'relative',
              zIndex: 10
            }}>
              <img
                src={currentContent.mapImage}
                alt="UAE Map"
                style={{
                  width: '100%',
                  maxWidth: '700px',
                  borderRadius: '20px',
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)',
                  margin: '0 auto',
                  display: 'block'
                }}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WhereToBuy;
